import {
  AttentionBlock,
  BlockTitle,
  Button,
  QAGroup,
  QAPair,
  Surface,
  formatDate,
  formatTime,
  plural,
  titleCase,
} from '@westhetics/ui'
import { useId, useState, type ReactNode } from 'react'
import type { Anamnesis, AnamnesisReadingQuestion, AnamnesisReadingSection } from './api'
import {
  COPY_EVENT_LABELS,
  attentionTitle,
  deliveryLine,
  formatDateTime,
  readingDate,
  recordChangesSentence,
  recordIssueSentence,
} from './labels'

/**
 * Leitura da anamnese respondida (docs/areas/anamnese.md, "Lado B · A clínica lê"; RN-ANA-16, 20 e
 * 21). Arquétipo: leitura. A pergunta é "posso aplicar nessa paciente?": a prova no cabeçalho, o
 * que pede atenção em Plum no topo, as respostas por bloco em ordem clínica e o contexto recolhido.
 * Somente leitura: nada aqui se edita.
 */

/**
 * Ações que dependem de áreas que ainda não existem: ficam na tela, desabilitadas, com o motivo
 * escrito ao lado (docs/styleguide/03-componentes.md, botão). A geração de documentos é da área de
 * documentos (RN-ANA-23), e o PDF responde 501 enquanto o renderizador não entra
 * (docs/api/anamneses.md, `GET /anamneses/:id/pdf`). Quando entrarem, o motivo sai e a ação liga.
 */
const GENERATE_DOCUMENT_UNAVAILABLE = 'A geração de documentos ainda não está no ar.'
const PDF_UNAVAILABLE =
  'A geração de PDF ainda não está no ar; por enquanto a leitura é nesta tela.'

const reasonClass = 'font-body text-eyebrow text-label leading-[1.5] font-light tracking-normal'
const lineClass = 'font-body text-secondary text-[12px] leading-[1.6] font-light'

/** "Anamnese de 28/04/2026 · 09:51" e, à parte, "hash a3f9c1e7…4b82d5". */
function evidenceHeaderParts(anamnesis: Anamnesis) {
  const header = anamnesis.evidenceHeader
  if (header) {
    const [main, hash] = header.split(' · hash ')
    return { main: main ?? header, hash: hash ? `hash ${hash}` : null }
  }
  const submittedAt = anamnesis.evidence?.submittedAt ?? anamnesis.completedAt
  if (!submittedAt) return { main: 'Anamnese preenchida', hash: null }
  const date = new Date(submittedAt)
  return {
    main: `Anamnese de ${formatDate(date)} · ${formatTime(date)}`,
    hash: anamnesis.evidence ? `hash ${anamnesis.evidence.shortHash}` : null,
  }
}

function Answer({ question }: { question: AnamnesisReadingQuestion }) {
  const variants = question.variants ?? []
  if (variants.length === 0) return <>{question.answerText}</>
  return (
    <>
      {question.answerText}
      <span className={`${lineClass} mt-2 block`}>
        O sistema anterior guardou {variants.length + 1} respostas para esta pergunta. As outras:{' '}
        {variants.map((variant) => variant.answerText).join(' · ')}
      </span>
    </>
  )
}

function Pairs({ questions }: { questions: AnamnesisReadingQuestion[] }) {
  return (
    <div className="tablet:grid-cols-2 grid grid-cols-1 gap-x-6">
      {questions.map((question, index) => (
        <QAPair
          key={question.questionId ?? `${question.shortLabel}-${index}`}
          className="border-t border-b-0"
          question={question.shortLabel}
          answer={<Answer question={question} />}
          risk={question.isFlagged}
          title={question.label}
        />
      ))}
    </div>
  )
}

/** Um bloco recolhido: o nome à vista, o conteúdo ao abrir. */
function Collapsed({
  name,
  summary,
  children,
}: {
  name: string
  summary: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  return (
    <div className="border-hairline border-b last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className="hover:bg-pampas focus-visible:bg-pampas min-h-touch-desk max-desk:min-h-touch flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3 text-left"
      >
        <span className="font-display text-lead text-ink leading-[1.35] font-normal">{name}</span>
        <span className={reasonClass}>{open ? 'recolher' : `${summary} · abrir`}</span>
      </button>
      {open ? (
        <div id={panelId} className="px-4 pb-2">
          {children}
        </div>
      ) : null}
    </div>
  )
}

export function AnamnesisReading({
  anamnesis,
  onBack,
}: {
  anamnesis: Anamnesis
  onBack: () => void
}) {
  const generateReasonId = useId()
  const pdfReasonId = useId()
  const name = titleCase(anamnesis.patient.name)
  const header = evidenceHeaderParts(anamnesis)
  const evidence = anamnesis.evidence ?? null
  const attention = anamnesis.attention ?? []
  const mainSections = anamnesis.mainSections ?? []
  const collapsedSections = anamnesis.collapsedSections ?? []
  const changes = recordChangesSentence(anamnesis.recordChanges)
  const openIssues = anamnesis.recordIssuesResolvedAt ? [] : (anamnesis.recordIssues ?? [])
  const delivery = deliveryLine(anamnesis)

  const identification = [
    name,
    anamnesis.patient.ageAtSendTime !== null ? `${anamnesis.patient.ageAtSendTime} anos` : null,
    anamnesis.procedureAtSendTime
      ? `procedimento na época: ${anamnesis.procedureAtSendTime.name}`
      : 'sem procedimento na época',
  ]
    .filter(Boolean)
    .join(' · ')

  const trail = [
    evidence ? `Protocolo ${evidence.protocolNumber}` : null,
    evidence ? `respondida em ${evidence.device}` : null,
    evidence && evidence.copy.event
      ? `cópia para ${evidence.copy.email}: ${COPY_EVENT_LABELS[evidence.copy.event] ?? evidence.copy.event}`
      : evidence
        ? `cópia para ${evidence.copy.email}`
        : null,
  ].filter(Boolean)

  const sending = [
    anamnesis.createdBy
      ? `Link enviado em ${formatDateTime(anamnesis.createdAt)} por ${titleCase(anamnesis.createdBy.name)}`
      : anamnesis.isImported
        ? `Importada do sistema antigo em ${formatDateTime(anamnesis.createdAt)}`
        : `Link enviado em ${formatDateTime(anamnesis.createdAt)}`,
    delivery,
    anamnesis.practitioner
      ? `profissional: ${titleCase(anamnesis.practitioner.name)}`
      : 'sem profissional responsável',
  ].filter(Boolean)

  return (
    <article aria-label="Ficha de anamnese" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <Button variant="text" onClick={onBack}>
          ← Voltar à lista
        </Button>
        <nav className={lineClass} aria-label="Anamnese aberta">
          {name} / anamneses / <span className="text-ink">{readingDate(anamnesis)}</span>
        </nav>
      </div>

      <BlockTitle
        className="max-desk:flex-col max-desk:items-start flex-wrap"
        title="Ficha de anamnese"
        note={identification}
        trailing={
          <div className="flex flex-col items-start gap-[6px]">
            <Button disabled aria-describedby={generateReasonId}>
              Gerar documento com esta anamnese
            </Button>
            <span id={generateReasonId} className={reasonClass}>
              {GENERATE_DOCUMENT_UNAVAILABLE}
            </span>
          </div>
        }
      />

      <Surface padding="none" className="px-4 py-3" role="group" aria-label="Prova do envio">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="font-body text-ink text-[12px] leading-[1.5] font-light">
            {header.main}
            {header.hash ? <span className="text-secondary"> · {header.hash}</span> : null}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button variant="text" disabled aria-describedby={pdfReasonId}>
              Imprimir
            </Button>
            <Button variant="text" disabled aria-describedby={pdfReasonId}>
              Baixar PDF
            </Button>
          </div>
        </div>
        {trail.length > 0 ? <p className={`${lineClass} mt-2`}>{trail.join(' · ')}</p> : null}
        <p className={`${lineClass} mt-1`}>{sending.join(' · ')}</p>
        <p id={pdfReasonId} className={`${reasonClass} mt-2`}>
          {PDF_UNAVAILABLE}
        </p>
      </Surface>

      {attention.length > 0 ? (
        <AttentionBlock title={attentionTitle(attention.length)}>
          <ul>
            {attention.map((item, index) => (
              <li key={`${item.questionKey}-${index}`}>· {item.label}</li>
            ))}
          </ul>
        </AttentionBlock>
      ) : null}

      {changes || openIssues.length > 0 || anamnesis.isImported ? (
        <AttentionBlock variant="lilac" role="note" aria-label="O que este envio mudou">
          {anamnesis.isImported ? (
            <p>
              Ficha importada do sistema anterior: é lida com as perguntas de então e não tem
              protocolo nem hash.
            </p>
          ) : null}
          {changes ? <p>{changes}.</p> : null}
          {openIssues.map((issue) => (
            <p key={issue}>
              {recordIssueSentence(issue)} O cadastro não foi alterado: resolva nos dados pessoais
              da paciente.
            </p>
          ))}
        </AttentionBlock>
      ) : null}

      {mainSections.map((section: AnamnesisReadingSection) => (
        <QAGroup key={section.key} title={section.name} className="border-t-0 pt-2">
          <Pairs questions={section.questions} />
        </QAGroup>
      ))}

      {collapsedSections.length > 0 || evidence ? (
        <section aria-label="Blocos recolhidos" className="pt-2">
          <h3 className="eyebrow">Blocos recolhidos</h3>
          <p className={`${lineClass} mt-[6px]`}>
            Contexto de atendimento, fora da decisão clínica. Clique para abrir.
          </p>
          <div className="border-hairline bg-surface mt-2 border">
            {collapsedSections.map((section) => (
              <Collapsed
                key={section.key}
                name={section.name}
                summary={plural(section.questions.length, 'resposta')}
              >
                <Pairs questions={section.questions} />
              </Collapsed>
            ))}
            {evidence ? (
              <Collapsed
                name="Declaração aceita"
                summary={`versão ${evidence.declarationAcceptance.version}`}
              >
                <p className="font-body text-body text-ink border-hairline border-t py-[14px] font-light">
                  {evidence.declarationAcceptance.text}
                </p>
                <p className={`${lineClass} pb-2`}>
                  Aceita em {formatDateTime(evidence.declarationAcceptance.acceptedAt)} · versão{' '}
                  {evidence.declarationAcceptance.version}
                </p>
              </Collapsed>
            ) : null}
          </div>
        </section>
      ) : null}

      <p className={lineClass}>
        Somente leitura: a anamnese preenchida é registro com prova e não é editada pela clínica.
        Para atualizar as respostas, envie uma nova anamnese.
      </p>
    </article>
  )
}
